import { useEffect, useState } from 'react';
import { getTreatmentPathwayOptions } from './treatmentPathwayOptions';

/**
 * Loads the treatment pathway dropdown options for a report from the API.
 *
 * The endpoint returns only the pathways that are usable for the report's
 * client (i.e. pathways that have a service charge configured for that
 * client, or fall back to the global TreatmentPathwayServiceLines table).
 * This mirrors the publish-time check in
 * ServiceChargeOperation.GetTreatmentPathwayServiceChargeLines so users
 * can no longer pick a pathway that will fail charge validation on publish.
 *
 * If the API call fails (network/server error), we fall back to the static
 * hardcoded list so the picker is never empty.
 *
 * @param {object} api          - api instance with webAPIFetch
 * @param {string} reportId     - report GUID
 * @param {boolean} isCYP       - whether the patient is CYP (only used by fallback)
 * @returns {{ options: Array<{value:number,label:string}>, loading: boolean, error: any }}
 */
export const useTreatmentPathwayOptions = (api, reportId, isCYP) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!api || !reportId) {
        setOptions(getTreatmentPathwayOptions(isCYP));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.webAPIFetch(
          `/api/PatientCaseReports/${reportId}/lookups/treatment-pathways`
        );

        if (!response.ok) {
          throw new Error(`Treatment pathways request failed: ${response.status}`);
        }

        const data = await response.json();
        if (cancelled) {
          return;
        }
        setOptions(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(err);
        setOptions(getTreatmentPathwayOptions(isCYP));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [api, reportId, isCYP]);

  return { options, loading, error };
};
